import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';

export default function SIPPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-purple-600">SIP Plans</span>
            </h1>
            <p className="text-xl text-gray-600">
              Systematic Investment Plan - The smart way to invest
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* What is SIP */}
            <section className="bg-purple-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What is SIP?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                SIP (Systematic Investment Plan) is a method of investing in mutual funds where you invest a 
                <strong> fixed amount regularly</strong> (monthly, quarterly) instead of a lump sum. It's like a 
                recurring deposit, but with the potential for much higher returns.
              </p>
              <div className="bg-white rounded-xl p-6 border-2 border-purple-200">
                <h3 className="font-bold text-lg mb-3">Example:</h3>
                <p className="text-gray-700">
                  Instead of investing ₹60,000 at once, you invest <strong>₹5,000 every month for 12 months</strong>. 
                  This builds discipline and reduces the impact of market volatility.
                </p>
              </div>
            </section>

            {/* Power of Compounding */}
            <section className="bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4">💰 The Magic of Compounding</h2>
              <div className="bg-white text-gray-900 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 text-purple-600">See How Your Money Grows!</h3>
                <div className="space-y-4">
                  <div className="border-b pb-4">
                    <div className="text-sm text-gray-600 mb-1">Monthly SIP: ₹5,000</div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">After 10 years @ 12%:</span>
                      <span className="text-2xl font-bold text-green-600">₹11.6 Lakhs</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">You invested: ₹6L | Returns: ₹5.6L</div>
                  </div>
                  <div className="border-b pb-4">
                    <div className="text-sm text-gray-600 mb-1">Monthly SIP: ₹10,000</div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">After 20 years @ 12%:</span>
                      <span className="text-2xl font-bold text-green-600">₹99.9 Lakhs</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">You invested: ₹24L | Returns: ₹75.9L</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Monthly SIP: ₹15,000</div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">After 25 years @ 12%:</span>
                      <span className="text-2xl font-bold text-green-600">₹2.84 Crores</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">You invested: ₹45L | Returns: ₹2.39Cr</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Benefits of SIP */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Why SIP is Better Than Lump Sum</h2>
              <div className="grid gap-4">
                <div className="flex items-start border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition-colors">
                  <span className="text-3xl mr-4">💰</span>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Start Small</h3>
                    <p className="text-gray-600">Begin with as little as ₹500/month. No need for large capital.</p>
                  </div>
                </div>
                <div className="flex items-start border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition-colors">
                  <span className="text-3xl mr-4">🎯</span>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Rupee Cost Averaging</h3>
                    <p className="text-gray-600">Buy more units when market is low, fewer when high. Averages out your cost.</p>
                  </div>
                </div>
                <div className="flex items-start border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition-colors">
                  <span className="text-3xl mr-4">🤖</span>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Automated Discipline</h3>
                    <p className="text-gray-600">Auto-debit from bank. No need to remember or time the market.</p>
                  </div>
                </div>
                <div className="flex items-start border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition-colors">
                  <span className="text-3xl mr-4">💎</span>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Power of Compounding</h3>
                    <p className="text-gray-600">Returns generate more returns over time. Start early to maximize benefits.</p>
                  </div>
                </div>
                <div className="flex items-start border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition-colors">
                  <span className="text-3xl mr-4">🛡️</span>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Less Market Risk</h3>
                    <p className="text-gray-600">Spreading investments reduces impact of market volatility.</p>
                  </div>
                </div>
                <div className="flex items-start border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition-colors">
                  <span className="text-3xl mr-4">🎁</span>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Flexible</h3>
                    <p className="text-gray-600">Pause, increase, decrease, or stop anytime. No lock-in (except ELSS).</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Goal-Based SIP */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">SIP for Different Goals</h2>
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
                  <h3 className="font-bold text-lg mb-2">🏠 Home Down Payment (5-7 years)</h3>
                  <p className="text-gray-700 mb-2">Target: ₹20 lakhs | Recommended SIP: ₹20,000/month</p>
                  <p className="text-sm text-gray-600">Invest in: Balanced Hybrid Funds or Multi-Cap Equity Funds</p>
                </div>
                <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-600">
                  <h3 className="font-bold text-lg mb-2">🎓 Child Education (10-15 years)</h3>
                  <p className="text-gray-700 mb-2">Target: ₹50 lakhs | Recommended SIP: ₹15,000-20,000/month</p>
                  <p className="text-sm text-gray-600">Invest in: Diversified Equity Funds, Children's Funds</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-6 border-l-4 border-purple-600">
                  <h3 className="font-bold text-lg mb-2">🏖️ Retirement (20-30 years)</h3>
                  <p className="text-gray-700 mb-2">Target: ₹2-3 crores | Recommended SIP: ₹15,000-25,000/month</p>
                  <p className="text-sm text-gray-600">Invest in: Equity Funds, Index Funds, Retirement Funds</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
                  <h3 className="font-bold text-lg mb-2">🎯 Short-term Goals (1-3 years)</h3>
                  <p className="text-gray-700 mb-2">Target: Varies | Recommended SIP: Based on goal</p>
                  <p className="text-sm text-gray-600">Invest in: Debt Funds, Liquid Funds, Ultra Short Duration Funds</p>
                </div>
              </div>
            </section>

            {/* How to Start */}
            <section className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Start Your SIP</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 mr-4 font-bold">1</div>
                  <div>
                    <h3 className="font-bold mb-1">Define Your Goal</h3>
                    <p className="text-gray-600">What are you saving for? When do you need the money?</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 mr-4 font-bold">2</div>
                  <div>
                    <h3 className="font-bold mb-1">Complete KYC</h3>
                    <p className="text-gray-600">One-time documentation with PAN, Aadhaar, bank details</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 mr-4 font-bold">3</div>
                  <div>
                    <h3 className="font-bold mb-1">Choose the Right Funds</h3>
                    <p className="text-gray-600">We'll recommend funds based on your goals and risk profile</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 mr-4 font-bold">4</div>
                  <div>
                    <h3 className="font-bold mb-1">Set Up Auto-Debit</h3>
                    <p className="text-gray-600">Money gets invested automatically every month from your bank</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 mr-4 font-bold">5</div>
                  <div>
                    <h3 className="font-bold mb-1">Review Annually</h3>
                    <p className="text-gray-600">We'll review your portfolio and suggest changes if needed</p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="text-center bg-purple-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Start Your SIP Journey Today
              </h2>
              <p className="text-purple-100 mb-6">
                Even ₹500/month can grow into lakhs over time. Let's get started!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/#contact"
                  className="inline-block bg-white text-purple-600 px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-lg font-semibold"
                >
                  Plan My SIP
                </Link>
                <a
                  href="https://wa.me/919248852187?text=Hi, I want to start a SIP. Please guide me."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 text-white px-8 py-4 rounded-xl hover:bg-green-600 transition-all shadow-lg font-semibold"
                >
                  WhatsApp Now
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
